import { BaseNumber, CheckedAddress, promiseAll } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions } from '@tanstack/react-query'
import { mapValues } from 'remeda'
import { Address } from 'viem'
import { Config } from 'wagmi'
import { readContract } from 'wagmi/actions'

import { pointsRewardsResponseSchema } from './pointsRewardsResponseSchema'
import { SEASON_1_POINTS_CLAIMABLE } from '@/config/chain/flags'
import { spark2ApiUrl } from '@/config/consts'
import { pointsSparkRewardsConfig } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { TokenDefinition } from '@/domain/types/TokenDefinition'

export interface ConvertPointsRewardsQueryOptionsParams {
  wagmiConfig: Config
  chainId: number
  account?: CheckedAddress
  spkToken: TokenDefinition
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function convertPointsRewardQueryOptions({
  wagmiConfig,
  chainId,
  account,
  spkToken,
}: ConvertPointsRewardsQueryOptionsParams) {
  return queryOptions({
    queryKey: convertPointsRewardQueryKey({ account, chainId }),
    queryFn: () => convertPointsRewardQueryFunction({ account, wagmiConfig, chainId, spkToken }),
  })
}

export function convertPointsRewardQueryKey({ account, chainId }: { account?: Address; chainId: number }): QueryKey {
  return ['convert-points-rewards', chainId, account]
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export async function convertPointsRewardQueryFunction({
  account,
  wagmiConfig,
  chainId,
  spkToken,
}: {
  account?: CheckedAddress
  wagmiConfig: Config
  chainId: number
  spkToken: TokenDefinition
}) {
  if (!SEASON_1_POINTS_CLAIMABLE || !account) {
    return null
  }

  const expectedMerkleRoot = await readContract(wagmiConfig, {
    address: getContractAddress(pointsSparkRewardsConfig.address, chainId),
    abi: pointsSparkRewardsConfig.abi,
    functionName: 'merkleRoot',
    chainId,
  })

  const res = await fetch(`${spark2ApiUrl}/referrals/roots/${expectedMerkleRoot}/${account}/`)
  if (!res.ok) {
    throw new Error('Failed to fetch rewards')
  }

  const claimData = pointsRewardsResponseSchema.parse(await res.json())

  const { preClaimedS1, preClaimedS2 } = mapValues(
    await promiseAll(
      {
        preClaimedS1: () =>
          readContract(wagmiConfig, {
            address: getContractAddress(pointsSparkRewardsConfig.address, chainId),
            abi: pointsSparkRewardsConfig.abi,
            functionName: 'cumulativeClaimed',
            args: [account, spkToken.address, 1n],
            chainId,
          }),
        preClaimedS2: () =>
          readContract(wagmiConfig, {
            address: getContractAddress(pointsSparkRewardsConfig.address, chainId),
            abi: pointsSparkRewardsConfig.abi,
            functionName: 'cumulativeClaimed',
            args: [account, spkToken.address, 2n],
            chainId,
          }),
      },
      { concurrency: 'unbounded' },
    ),
    (preClaimed) => spkToken.fromBaseUnit(BaseNumber(preClaimed)),
  )

  const rewards = claimData.map((claim) => {
    return {
      merkleRoot: claim.root_hash,
      epoch: claim.epoch,
      cumulativeAmount: claim.amount_normalized,
      restrictedCountryCodes: claim.restricted_country_codes,
      preClaimed: claim.epoch === 1 ? preClaimedS1 : preClaimedS2,
      merkleProof: claim.proof,
      chainId,
    }
  })

  return {
    reward: rewards.filter((r) => expectedMerkleRoot === r.merkleRoot)[0] ?? null,
    totalClaimed: preClaimedS1.plus(preClaimedS2),
  }
}
